import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Navigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { isAuthenticated, setRole, getRole } from "./auth";
import {
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { glassAuthCard, glassTextField } from "./glass";
import { APP_TOP_NAV_HEIGHT_PX } from "./appChrome";

const TAB_SIGN_IN = 0;
const TAB_SIGN_UP = 1;

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() =>
    searchParams.get("tab") === "signup" ? TAB_SIGN_UP : TAB_SIGN_IN
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [passwordSignup, setPasswordSignup] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  /** `customer` = homeowner; `admin` = lawn care operator / business */
  const [signupAccountType, setSignupAccountType] = useState("customer");

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (searchParams.get("tab") === "signup") {
      setTab(TAB_SIGN_UP);
    }
  }, [searchParams]);

  const syncUrlToTab = useCallback(
    (value) => {
      if (value === TAB_SIGN_UP) {
        setSearchParams({ tab: "signup" }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    },
    [setSearchParams]
  );

  const handleTabChange = (_, value) => {
    setTab(value);
    setError("");
    syncUrlToTab(value);
  };

  if (isAuthenticated()) {
    const dest = location.state?.from?.pathname;
    const r = getRole();
    const fallback =
      r === "superadmin" ? "/platform" : "/dashboard";
    const safe = typeof dest === "string" && dest.startsWith("/") ? dest : fallback;
    return <Navigate to={safe} replace />;
  }

  const completeLogin = async (loginEmail, loginPassword) => {
    const response = await axios.post("/login", {
      email: loginEmail,
      password: loginPassword,
    });
    localStorage.setItem("token", response.data.token);
    const role =
      response.data.role === "superadmin"
        ? "superadmin"
        : response.data.role === "admin"
          ? "admin"
          : "customer";
    setRole(role);
    const fallbackHome = role === "superadmin" ? "/platform" : "/dashboard";
    const dest = location.state?.from?.pathname;
    const target = typeof dest === "string" && dest.startsWith("/") ? dest : fallbackHome;
    navigate(target, { replace: true });
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await completeLogin(email.trim().toLowerCase(), password);
    } catch (err) {
      const msg =
        err.response?.data?.message || "Login failed. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async (event) => {
    event.preventDefault();
    setError("");
    if (passwordSignup !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (passwordSignup.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    const emailNorm = email.trim().toLowerCase();
    if (!firstname.trim()) {
      setError("First name is required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post("/signup", {
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        email: emailNorm,
        password: passwordSignup,
      });
      await completeLogin(emailNorm, passwordSignup);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        "Could not create account. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const grassUrl = `${process.env.PUBLIC_URL || ""}/hero-lawn.png`;

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: `calc(100dvh - ${APP_TOP_NAV_HEIGHT_PX}px)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        pt: 4,
        pb: 4,
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          backgroundImage: `url(${grassUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          background:
            "linear-gradient(135deg, rgba(10, 25, 10, 0.78) 0%, rgba(20, 50, 20, 0.68) 45%, rgba(10, 35, 10, 0.82) 100%)",
        }}
      />
      <Card
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: 420,
          width: "100%",
          ...glassAuthCard,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3, position: "relative" }}>
          <Typography
            variant="h5"
            gutterBottom
            fontWeight={700}
            letterSpacing="-0.03em"
            sx={{
              textShadow: "0 1px 0 rgba(255,255,255,0.85)",
              color: "rgba(18, 32, 24, 0.95)",
            }}
          >
            {tab === TAB_SIGN_IN ? "Welcome back" : "Create an account"}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, textShadow: "0 1px 0 rgba(255,255,255,0.6)" }}
          >
            {tab === TAB_SIGN_IN
              ? "Sign in to your Clibbins operator or customer account."
              : "Choose whether you hire lawn care (homeowner) or you run the business (operator), then create your account."}
          </Typography>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              mb: 2,
              minHeight: 48,
              p: 0.5,
              borderRadius: 2.5,
              bgcolor: "rgba(255,255,255,0.45)",
              backdropFilter: "blur(20px) saturate(180%)",
              WebkitBackdropFilter: "blur(20px) saturate(180%)",
              border: "1px solid rgba(255,255,255,0.72)",
              boxShadow: `
                inset 0 1px 0 rgba(255, 255, 255, 0.9),
                0 2px 12px rgba(20, 60, 40, 0.08)
              `,
              "& .MuiTab-root": {
                minHeight: 44,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9rem",
                borderRadius: 2,
                color: "text.secondary",
                transition: "background-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease",
                "&.Mui-selected": {
                  color: "primary.dark",
                  bgcolor: "rgba(255,255,255,0.72)",
                  boxShadow: `
                    inset 0 1px 0 rgba(255, 255, 255, 0.95),
                    0 2px 8px rgba(0, 0, 0, 0.06)
                  `,
                },
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            <Tab label="Sign in" />
            <Tab label="Sign up" />
          </Tabs>
          {error ? (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
                bgcolor: "rgba(255, 235, 238, 0.92)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(211, 47, 47, 0.25)",
              }}
            >
              {error}
            </Alert>
          ) : null}
          {tab === TAB_SIGN_IN ? (
            <form onSubmit={handleSignIn}>
              <TextField
                id="email-input"
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                autoComplete="email"
                sx={{ mb: 2, ...glassTextField }}
              />
              <TextField
                id="password-input"
                type="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                autoComplete="current-password"
                sx={{ mb: 3, ...glassTextField }}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={submitting}
              >
                {submitting ? "Signing in…" : "Log in"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSignUp}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75, fontWeight: 600 }}>
                Account type
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={signupAccountType}
                onChange={(_, v) => v != null && setSignupAccountType(v)}
                sx={{ mb: 2 }}
                size="small"
                color="primary"
              >
                <ToggleButton value="customer">Homeowner / customer</ToggleButton>
                <ToggleButton value="admin">Lawn care operator</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, lineHeight: 1.45 }}>
                Operators get the business dashboard, quote page, and billing. Customers request services and track jobs.
                If you pick the wrong type, you can ask Clibbins support to switch you after signing in.
              </Typography>
              <TextField
                id="signup-firstname"
                label="First name"
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
                fullWidth
                required
                autoComplete="given-name"
                sx={{ mb: 2, ...glassTextField }}
              />
              <TextField
                id="signup-lastname"
                label="Last name"
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
                fullWidth
                autoComplete="family-name"
                sx={{ mb: 2, ...glassTextField }}
              />
              <TextField
                id="signup-email"
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                autoComplete="email"
                sx={{ mb: 2, ...glassTextField }}
              />
              <TextField
                id="signup-password"
                type="password"
                label="Password"
                value={passwordSignup}
                onChange={(e) => setPasswordSignup(e.target.value)}
                fullWidth
                required
                autoComplete="new-password"
                sx={{ mb: 2, ...glassTextField }}
                helperText="At least 6 characters."
              />
              <TextField
                id="signup-confirm"
                type="password"
                label="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                required
                autoComplete="new-password"
                sx={{ mb: 3, ...glassTextField }}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={submitting}
              >
                {submitting ? "Creating account…" : "Create account"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
